import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  ElementRef,
  EventEmitter,
  forwardRef,
  Inject,
  Input,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyAbstractFilterInput, GalaxyFilterValueInterface } from '@vendasta/galaxy/filter/chips';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { Category, CategoryType } from '@vendasta/category';
import { CategoryService } from '../../../shared-services/category.service';
import { map, startWith } from 'rxjs/operators';
import { BusinessCategoryModule, CategorySelectService } from '@galaxy/business-category';
import { CrmDependencies, CrmInjectionToken } from '../../../tokens-and-interfaces';
import { of, shareReplay, switchMap, tap } from 'rxjs';
import { GalaxyInfiniteScrollTriggerModule } from '@vendasta/galaxy/infinite-scroll-trigger';

@Component({
  template: `
    <glxy-form-field bottomSpacing="none">
      <mat-chip-grid #categoryGrid aria-label="Category selection">
        @if (loadingInitialValues()) {
          <mat-spinner [diameter]="24"></mat-spinner>
        } @else {
          @for (category of selectedCategories(); track category) {
            <mat-chip-row (removed)="remove(category.externalId)">
              {{ category.name }}
              <button matChipRemove [attr.aria-label]="'remove ' + category.name">
                <mat-icon>cancel</mat-icon>
              </button>
            </mat-chip-row>
          }
        }
        <input
          matInput
          #categoriesInput
          [placeholder]="'TABLE_FILTERS.SELECT_EXPRESSION_PLACEHOLDER' | translate"
          [formControl]="formControl"
          [matChipInputFor]="categoryGrid"
          [matAutocomplete]="auto"
          [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
          [matChipInputAddOnBlur]="false"
          data-testid="filter-category-input"
        />
      </mat-chip-grid>
      <mat-autocomplete #auto="matAutocomplete" (optionSelected)="selected($event)">
        @if (availableOptions() ?? [] | excludeCategories: selectedCategoryIDs(); as filteredOptions) {
          @for (option of filteredOptions; track option.categoryId; let isLast = $last) {
            <mat-option [value]="option?.externalId">
              @if (isLast) {
                <glxy-infinite-scroll-trigger (isVisible)="fetchMoreCategories()"></glxy-infinite-scroll-trigger>
              }
              {{ option?.fullName }}
            </mat-option>
          }
        }
      </mat-autocomplete>
    </glxy-form-field>
  `,
  providers: [
    {
      provide: GalaxyAbstractFilterInput,
      useExisting: forwardRef(() => CategoriesFilterInputComponent),
    },
  ],
  imports: [
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    MatInputModule,
    GalaxyFormFieldModule,
    MatAutocompleteModule,
    MatChipsModule,
    MatIconModule,
    MatProgressSpinnerModule,
    GalaxyInfiniteScrollTriggerModule,
    BusinessCategoryModule,
  ],
})
export class CategoriesFilterInputComponent implements GalaxyAbstractFilterInput {
  @ViewChild('categoriesInput') categoriesInput!: ElementRef<HTMLInputElement>;
  @Input() set fieldId(value: string) {
    this._fieldId.set(value);
  }

  @Output() valueChanges = new EventEmitter<GalaxyFilterValueInterface[]>();
  formControl: FormControl<string> = new FormControl<string>('', { nonNullable: true });
  separatorKeysCodes: number[] = [ENTER, COMMA];

  loadingInitialValues = signal(true);
  searchTerm = toSignal(this.formControl.valueChanges, { initialValue: '' });
  _fieldId = signal('');
  availableOptions = toSignal(
    this.categorySelectService.categories$.pipe(
      map((categories) => categories.categories),
      startWith([] as Category[]),
    ),
  );
  selectedCategoryIDs = signal<string[]>([]);
  selectedCategoriesIDs$ = toObservable(this.selectedCategoryIDs);
  categoriesData$ = this.selectedCategoriesIDs$.pipe(
    switchMap((selectedCategories) => {
      if (!selectedCategories?.length) {
        return of([]);
      }
      return this.categoryService.getMultiCategories(selectedCategories);
    }),
    tap(() => this.loadingInitialValues.set(false)),
    shareReplay(1),
  );
  categoriesData = toSignal(this.categoriesData$);
  selectedCategories = computed(() => {
    const selectedIds = this.selectedCategoryIDs();
    const categoryData = this.categoriesData();
    const selectedCategories = selectedIds.map((id) => {
      const category = categoryData?.find((option) => option?.externalId === id);
      if (category) {
        return category;
      }
      return { fullName: id, name: id, externalId: id };
    });
    return selectedCategories;
  });

  @Input() set initialValue(value: GalaxyFilterValueInterface[]) {
    const categoryIds = (value ?? []).map((v) => v.string || '');
    this.selectedCategoryIDs.set(categoryIds);
  }

  constructor(
    @Inject(CrmInjectionToken) private readonly config: CrmDependencies,
    private readonly categoryService: CategoryService,
    private readonly categorySelectService: CategorySelectService,
  ) {
    this.categorySelectService.categoryType = CategoryType.CATEGORY_TYPE_V_CATEGORY;
    this.config.locale$
      ?.pipe(
        takeUntilDestroyed(),
        tap((locale) => this.categorySelectService.language$$.next(locale)),
      )
      .subscribe();

    effect(() => {
      const selectedSalespeopleIDs = this.selectedCategoryIDs();
      this.valueChanges.emit(selectedSalespeopleIDs.map((s) => ({ string: s })));
    });

    effect(() => {
      const searchTerm = this.searchTerm();
      this.categorySelectService.fetch(searchTerm);
    });
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    const currentCategories = this.selectedCategoryIDs();
    const hasCategory = currentCategories.some((c) => c === event.option.value);
    if (event.option.value && !hasCategory) {
      this.selectedCategoryIDs.set([...currentCategories, event.option.value]);
    }
    this.categoriesInput.nativeElement.value = '';
    this.formControl.setValue('');
  }

  remove(categoryId: string) {
    const currentCategories = this.selectedCategoryIDs();
    const listWithoutSelectedCategory = currentCategories.filter((c) => c !== categoryId);
    this.selectedCategoryIDs.set([...listWithoutSelectedCategory]);
  }

  clearValue() {
    this.formControl.setValue('');
  }

  fetchMoreCategories(): void {
    this.categorySelectService.fetchNext();
  }
}
